<template>
  <div ref="entra_form">
    <b-overlay
      ref="overlay_index"
      :show="showLoading"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <b-card>
        <b-row>
          <b-col
            md="1"
            class="d-flex justify-content-center align-items-center mt-1"
          >
            <b-button variant="danger" @click="$router.go(-1)">Atrás</b-button>
          </b-col>

          <b-col md="2">
            <b-form-group label="Código de Documento">
              <b-form-input
                v-model="form.documentCode"
                placeholder="Ingrese Código"
                trim
              />
            </b-form-group>
          </b-col>

          <b-col md="1">
            <b-form-group label="DNI Cliente">
              <b-form-input v-model="form.clientDni" disabled />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Nombre Cliente">
              <b-form-input v-model="form.clientName" disabled />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Establecimiento">
              <b-form-input v-model="form.establishmentName" disabled />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Tipo de Moneda">
              <b-form-input v-model="form.currencyType" disabled />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            class="d-flex justify-content-end align-items-center mt-1"
          >
            <b-button variant="primary" @click="validateDocument"
              >Validar</b-button
            >
            <b-button
              :disabled="!isValidated || !canCompleteSale"
              variant="success"
              class="ml-1"
              @click="realizarVenta"
            >
              Vender
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- Tabla de Ítems -->
      <b-card class="mt-1" v-if="tableItems.length > 0">
        <b-table
          :items="tableItems"
          :fields="fields"
          responsive
          hover
          class="border border-2 rounded mt-2"
        >
          <template #cell(status)="data">
            <b-badge
              :variant="
                data.item.qtyAvailable >= data.item.qty ? 'success' : 'danger'
              "
            >
              {{
                data.item.qtyAvailable >= data.item.qty
                  ? "Disponible"
                  : "No Disponible"
              }}
            </b-badge>
          </template>

          <!-- Acciones -->
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="info"
              @click="viewStock(data.item)"
              v-b-tooltip
              title="Ver Stock"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
            <b-button
              size="sm"
              variant="warning"
              class="ml-2"
              @click="editItem(data.item)"
              v-b-tooltip
              title="Editar Cantidad"
            >
              <feather-icon icon="Edit2Icon" />
            </b-button>
            <b-button
              size="sm"
              variant="danger"
              class="ml-2"
              @click="requestStock(data.item)"
              v-b-tooltip
              title="Solicitar Stock"
              v-if="data.item.qtyAvailable < data.item.qty"
            >
              <feather-icon icon="TruckIcon" />
            </b-button>
          </template>
        </b-table>
      </b-card>

      <b-row v-if="calculatedItems.length > 0">
        <b-col sm="12">
          <b-card class="mt-1">
            <h4 class="font-weight-bold">Resumen de Costos</h4>
            <b-table
              small
              :items="calculatedItems"
              :fields="summary"
              responsive
              hover
              class="border border-2 rounded mt-2"
              head-variant="light"
              style="font-size: 1.1rem"
            >
              <template #cell(unitPrice)="data">
                {{ currencySymbol }} {{ data.item.unitPrice }}
              </template>
              <template #cell(cost)="data">
                {{ currencySymbol }} {{ data.item.cost }}
              </template>
            </b-table>
            <b-row class="mt-3">
              <b-col
                class="text-right font-weight-bold"
                style="font-size: 1.25rem"
              >
                Total:
              </b-col>
              <b-col
                class="text-left text-primary font-weight-bold"
                style="font-size: 1.5rem"
              >
                {{ currencySymbol }} {{ totalCost.toFixed(2) }}
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>

    <!-- Modal para ver stock -->
    <b-modal v-model="showStockModal" title="Stock Disponible" hide-footer>
      <b-table :items="stockData" :fields="stockFields" responsive striped />
    </b-modal>

    <!-- Modal para editar cantidad -->
    <b-modal v-model="showEditModal" title="Editar Cantidad" @ok="saveEdit">
      <b-form-group label="Cantidad">
        <b-form-input v-model="editItemData.qty" type="number" min="0" />
      </b-form-group>
    </b-modal>

    <!-- Modal para solicitar stock -->
    <b-modal
      v-model="showRequestStockModal"
      title="Solicitar Stock"
      @ok="confirmStockRequest"
    >
      <b-form-group label="Seleccionar Tienda de Origen">
        <b-form-select
          v-model="selectedStore"
          :options="storeOptions"
          @change="updateStockForSelectedStore"
        />
      </b-form-group>

      <b-form-group label="Cantidad Disponible en Tienda Seleccionada">
        <b-form-input v-model="storeStockAvailable" disabled />
      </b-form-group>

      <b-form-group label="Cantidad a Solicitar">
        <b-form-input
          v-model="requestedQty"
          type="number"
          :min="minRequestQty"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import { BootstrapVue, BootstrapVueIcons, VBTooltip } from "bootstrap-vue";
import InventoryService from "@/services/InventoryService";
import SaleService from "@/services/SaleService";
import UserService from "@/services/UserService";

Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);

export default {
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      showLoading: false,
      showStockModal: false,
      showEditModal: false,
      showRequestStockModal: false,
      stockData: [],
      editItemData: {},
      requestItem: null,
      selectedStore: null,
      storeStockAvailable: 0,
      requestedQty: 0,
      minRequestQty: 0,
      storeOptions: [],
      arrayFilters: [],
      form: {
        documentCode: "",
        documentClient: "",
        clientName: "",
        establishmentName: "",
        currencyType: "",
      },
      quote: [],
      records: [],
      tableItems: [],
      isValidated: false,
      fields: [
        { key: "autopart.uniqueCode", label: "Código Único" },
        { key: "autopart.description", label: "Autoparte" },
        { key: "autopart.originalCode", label: "Código Original" },
        { key: "qty", label: "Cantidad Pedida" },
        { key: "autopart.brand", label: "Marca Autoparte" },
        { key: "qtyAvailable", label: "Stock Disponible" },
        { key: "unitPrice", label: "Precio Unitario" },
        { key: "status", label: "Estado" },
        { key: "actions", label: "Acciones" },
      ],
      stockFields: [
        { key: "establishments.name", label: "Establecimiento" },
        { key: "qtyAvailable", label: "Stock Disponible" },
        { key: "qtyLedger", label: "Stock en Libro" },
      ],
      summary: [
        { key: "uniqueCode", label: "Código Único" },
        { key: "description", label: "Descripción" },
        { key: "quantity", label: "Cantidad" },
        { key: "unitPrice", label: "Precio Unitario" },
        { key: "cost", label: "Costo (Cantidad x Precio)" },
      ],
    };
  },
  computed: {
    canCompleteSale() {
      return this.tableItems.every((item) => item.qtyAvailable >= item.qty);
    },

    currencySymbol() {
      return this.form.currencyType === "Dólar" ? "$" : "S/";
    },

    calculatedItems() {
      return this.tableItems.map((item) => ({
        description: `${item.autopart.description}`,
        uniqueCode: item.autopart.uniqueCode,
        quantity: item.qty || 0,
        unitPrice: parseFloat(item.unitPrice || 0).toFixed(2),
        cost: (
          parseFloat(item.qty || 0) * parseFloat(item.unitPrice || 0)
        ).toFixed(2),
      }));
    },

    totalCost() {
      return this.tableItems.reduce((sum, item) => {
        return sum + parseFloat(item.qty) * parseFloat(item.unitPrice);
      }, 0);
    },
  },
  methods: {
    async validateDocument() {
      if (!this.form.documentCode) {
        this.$swal(
          "Error",
          "Ingrese un código de documento antes de validar.",
          "error"
        );
        return;
      }
      this.quote = [];
      try {
        this.showLoading = true;
        const response = await SaleService.ValidateQuote(
          this.form.documentCode,
          this.$store
        );

        if (response && response.statusCode === 200 && response.data) {
          this.quote = response.data;

          if (this.quote.state == "Caja") {
            this.$swal(
              "Error",
              "No se puede validar esta proforma porque ya se vendió.",
              "error"
            );
            this.isValidated = false;
            this.showLoading = false;
            return;
          } else if (this.quote.state === "Pre-venta") {
            this.$swal(
              "Error",
              "No se puede validar esta proforma porque ya se encuentra en pre-venta.",
              "error"
            );
            this.isValidated = false;
            this.showLoading = false;
            return;
          }

          if (this.quote.detail && Array.isArray(this.quote.detail)) {
            this.tableItems = this.quote.detail.map((item) => ({
              autopart: {
                id: item.autopart.id,
                uniqueCode: item.autopart.uniqueCode,
                description: item.autopart.description,
                originalCode: item.autopart.originalCode,
                brand: item.autopart.AutopartBrand.name,
              },
              qty: item.qty || 0,
              unitPrice: item.unitPrice || 0,
              qtyAvailable: 0,
            }));
          }

          this.form.clientDni = this.quote.clientDni;
          this.form.clientName = this.quote.clientName;
          this.form.establishmentName =
            this.quote.establishmentOrigin?.name || "No especificado";
          this.form.establishmentOriginId = this.quote.establishmentOriginId;
          this.form.currencyType = this.quote.currencyType;

          if (this.quote.detail && Array.isArray(this.quote.detail)) {
            this.tableItems = this.quote.detail.map((item) => ({
              autopart: {
                id: item.autopart.id,
                uniqueCode: item.autopart.uniqueCode,
                description: `${item.autopart.uniqueCode}/${item.autopart.description}`,
                originalCode: item.autopart.originalCode,
                alterCode: item.autopart.alterCode,
                brand: item.autopart.AutopartBrand.name,
              },
              qty: item.qty,
              unitPrice: item.unitPrice,
              qtyAvailable: 0,
            }));

            await this.loadStockForCurrentStore();
          } else {
            this.tableItems = [];
            this.$swal(
              "Error",
              "No se encontraron autopartes en la cotización.",
              "error"
            );
            this.isValidated = false;
            return;
          }

          this.isValidated = true;
          this.$swal(
            "Éxito",
            "La proforma ha sido validada correctamente.",
            "success"
          );
        } else {
          this.isValidated = false;
          this.$swal(
            "Error",
            "No se pudo validar la proforma. Verifique el código ingresado.",
            "error"
          );
        }
      } catch (error) {
        console.error("Error al validar la proforma:", error);
        this.isValidated = false;
        this.$swal(
          "Error",
          "Ocurrió un problema al validar la proforma.",
          "error"
        );
      } finally {
        this.showLoading = false;
      }
    },

    async loadStockForCurrentStore() {
      if (!this.form.establishmentOriginId) {
        console.error("No se ha definido el establecimiento.");
        return;
      }

      this.arrayFilters = [
        {
          keyContains: "establishments.id",
          key: "equals",
          value: this.form.establishmentOriginId,
        },
        {
          keyContains: "autopart.originalCode",
          key: "in",
          value: this.tableItems.map((item) => item.autopart.originalCode),
        },
      ];

      try {
        const url = `?limit=100&page=1&order=desc&sort=id&filter=${JSON.stringify(
          this.arrayFilters
        )}`;
        const response = await InventoryService.getAllStocks(url, this.$store);

        if (response.data?.rows?.length > 0) {
          const stockData = response.data.rows;

          this.tableItems.forEach((item) => {
            const stockItem = stockData.find(
              (stock) =>
                stock.autopart.originalCode === item.autopart.originalCode
            );
            if (stockItem) {
              item.qtyAvailable = stockItem.qtyAvailable;
            }
          });
        }
      } catch (error) {
        console.error("Error al obtener el stock de la tienda actual:", error);
      }
    },

    async viewStock(item) {
      this.arrayFilters = [];
      this.arrayFilters.push({
        keyContains: "autopart.originalCode",
        key: "contains",
        value: item.autopart.originalCode,
      });

      this.arrayFilters.push({
        keyContains: "autopart.alterCode",
        key: "contains",
        value: item.autopart.alterCode,
      });
      const url = `?limit=100&page=1&order=desc&sort=id&filter=${JSON.stringify(
        this.arrayFilters
      )}`;
      const response = await InventoryService.getAllStocks(url, this.$store);

      const establishment = this.form.establishmentOriginId;
      this.stockData = (response.data?.rows || []).filter(
        (row) => row.establishments.id !== establishment
      );
      this.showStockModal = true;
    },
    async requestStock(item) {
      console.log(item);
      this.requestItem = item;

      this.arrayFilters = [];
      this.arrayFilters.push({
        keyContains: "autopart.originalCode",
        key: "contains",
        value: item.autopart.originalCode,
      });
      this.arrayFilters.push({
        keyContains: "autopart.alterCode",
        key: "contains",
        value: item.autopart.alterCode,
      });

      const url = `?limit=100&page=1&order=desc&sort=id&filter=${JSON.stringify(
        this.arrayFilters
      )}`;
      const response = await InventoryService.getAllStocks(url, this.$store);

      this.storeOptions = (response.data?.rows || [])
        .filter(
          (stock) =>
            stock.establishments.id !== this.form.establishmentOriginId &&
            stock.qtyAvailable > 0
        )
        .map((stock) => ({
          value: stock.establishments.id,
          text: `${stock.establishments.name} (${stock.qtyAvailable} disponibles)`,
        }));

      await this.updateStockForSelectedStore();
      this.showRequestStockModal = true;
    },
    editItem(item) {
      this.editItemData = { ...item };
      this.showEditModal = true;
    },
    saveEdit() {
      const index = this.tableItems.findIndex(
        (i) => i.autopart.id === this.editItemData.autopart.id
      );
      if (index !== -1) {
        this.$set(this.tableItems, index, { ...this.editItemData });
      }
    },
    async updateStockForSelectedStore() {
      if (!this.selectedStore) return;
      this.requestedQty = this.requestItem.qty - this.requestItem.qtyAvailable;
      this.minRequestQty = this.requestedQty;
      this.arrayFilters = [];
      this.arrayFilters.push({
        keyContains: "autopart.originalCode",
        key: "contains",
        value: this.requestItem.autopart.originalCode,
      });
      console.log("TIENDA:", this.selectedStore);
      this.arrayFilters.push({
        keyContains: "establishments.id",
        key: "equals",
        value: this.selectedStore,
      });

      const url = `?limit=100&page=1&order=desc&sort=id&filter=${JSON.stringify(
        this.arrayFilters
      )}`;
      const response = await InventoryService.getAllStocks(url, this.$store);
      this.storeStockAvailable = response.data?.rows[0]?.qtyAvailable || 0;
    },
    async confirmStockRequest() {
      if (!this.selectedStore) {
        this.$swal(
          "Error",
          "Seleccione un establecimiento de origen.",
          "error"
        );
        return;
      }

      if (this.requestedQty > this.storeStockAvailable) {
        this.$swal(
          "Error",
          "La cantidad solicitada excede el stock disponible en la tienda seleccionada.",
          "error"
        );
        return;
      }

      const userData = JSON.parse(localStorage.getItem("userData"));
      const registerById = userData?.data?.id || null;

      if (!registerById) {
        this.$swal("Error", "No se pudo obtener el usuario logueado.", "error");
        return;
      }

      const payload = {
        establishmentInternalMovementId: this.selectedStore,
        establishmentOriginId: this.form.establishmentOriginId,
        registerById: registerById,
        correlative: this.form.documentCode,
        autoparts: [
          {
            autopartId: this.requestItem.autopart.id,
            qty: parseInt(this.requestedQty),
            unitPrice: parseFloat(this.requestItem.unitPrice),
          },
        ],
      };

      try {
        this.showLoading = true;
        console.log("SI ENTRA");
        const response = await SaleService.RequestStock(payload, this.$store);
        console.log("RESPUESTA", response);
        if (response && response.statusCode === 200) {
          this.$swal(
            "Éxito",
            "Movimiento interno de stock realizado correctamente. Espere a la confirmación de la tienda",
            "success"
          );
          this.showRequestStockModal = false;
        } else {
          this.$swal(
            "Error",
            "No se pudo realizar el movimiento de stock.",
            "error"
          );
        }
      } catch (error) {
        console.error("Error al solicitar stock:", error);
        this.$swal("Error", "Ocurrió un problema al solicitar stock.", "error");
      } finally {
        this.showLoading = false;
      }
    },

    async realizarVenta() {
      const response = await UserService.getUsers("", this.$store);
      const users = response.data.rows;
      const extraPasswords = users
        .map((user) => user.extraPassword)
        .filter((password) => password !== null);

      if (extraPasswords.length === 0) {
        this.$swal("Error", "No hay contraseñas extras registradas.", "error");
        return;
      }

      const { value: inputPassword } = await this.$swal({
        title: "Ingrese la contraseña extra",
        input: "password",
        inputPlaceholder: "Ingrese la contraseña extra",
        showCancelButton: true,
        confirmButtonText: "Validar",
        cancelButtonText: "Cancelar",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
        },
        buttonsStyling: false,
        inputValidator: (value) => {
          if (!value) {
            return "Debe ingresar una contraseña.";
          }
          if (!extraPasswords.includes(value)) {
            return "Contraseña incorrecta. Intente nuevamente.";
          }
        },
      });

      if (!inputPassword) {
        return;
      }

      const matchingUser = users.find(
        (user) => user.extraPassword === inputPassword
      );

      if (!matchingUser) {
        this.$swal("Error", "Contraseña no válida.", "error");
        return;
      }

      const payload = {
        establishmentOriginId: this.form.establishmentOriginId,
        registerById: matchingUser.id,
        quoteId: this.quote.id,
        state: "Pre-venta",
        autoparts: this.tableItems.map((item) => ({
          autopartId: item.autopart.id,
          qty: item.qty,
          unitPrice: item.unitPrice,
        })),
      };

      try {
        this.showLoading = true;
        const response = await SaleService.saveSale(payload, this.$store);
        console.log("RESPUESTA", response);

        if (response && response.statusCode === 200) {
          this.$swal("Éxito", "Se envió a caja correctamente.", "success");
          this.$router.push("/venta");
        } else {
          this.$swal("Error", "No se pudo enviar a caja.", "error");
        }
      } catch (error) {
        console.error("Error al enviar a caja:", error);
        this.$swal("Error", "Ocurrió un problema al enviar a caja.", "error");
      } finally {
        this.showLoading = false;
      }
    },
  },
};
</script>
